/**
 * Branded primitives for the trading domain.
 *
 * The dashboard handles ₹300–400 Cr of live capital. A `number` that means
 * "rupees" must not be assignable to a parameter that wants "basis points"
 * or "price". TypeScript's structural typing is opted out of via brands so
 * the compiler enforces unit semantics across every boundary.
 *
 * Construction goes through the explicit `*` helpers below — they only check
 * *shape* (NaN / non-finite) and return the branded value unchanged. Real
 * arithmetic is delegated to `lib/math/money.ts` and `lib/math/price.ts`
 * which use `dnum` (money) and `decimal.js` (prices/quantities). Never use
 * native `+`/`-`/`*`/`/` on these primitives — an ESLint rule will be added
 * in P0-A.2 to enforce this.
 */

declare const __brand: unique symbol;
type Brand<T, B extends string> = T & { readonly [__brand]: B };

/* ============================================================================
 * Money — represented in MAJOR units (rupees, dollars). For "lakhs" and "crore"
 * use formatters; do not introduce LakhsINR / CroreINR as additional brands —
 * those are display units, not storage units.
 *
 * Sub-rupee precision: stored as JS `number`; arithmetic via `dnum` so the
 * dnum side-channel keeps full decimal precision. Display formatters know
 * how many places to render per asset class.
 * ============================================================================ */

export type INR = Brand<number, 'INR'>;
export type USD = Brand<number, 'USD'>;

/** Generic money brand — for parameters that genuinely don't care about the
 *  currency (e.g., "any positive amount in the user's base currency").
 *  Prefer the specific `INR` / `USD` brands wherever possible. */
export type Money<C extends string = string> = Brand<number, `Money<${C}>`>;

/* ============================================================================
 * Rate / sizing
 * ============================================================================ */

/** Basis points. 1 bp = 0.01%. */
export type BPS = Brand<number, 'BPS'>;

/** Percentage in [0, 100]. */
export type PercentPoints = Brand<number, 'PercentPoints'>;

/** Ratio (fractional) in [0, 1]. */
export type Ratio = Brand<number, 'Ratio'>;

/** Multiplier on a strategy's default sizing (e.g. 0.5x, 1.0x, 2.0x). */
export type LotMult = Brand<number, 'LotMult'>;
export type QtyMult = Brand<number, 'QtyMult'>;

/* ============================================================================
 * Price / qty — kept as STRING brands so the wire representation (also string,
 * via decimal.js) round-trips losslessly. Numerical operations go through
 * `lib/math/price.ts` which lifts to Decimal.
 *
 * Display formatters accept either branded string or branded number; the
 * underlying value always uses the string form internally for math safety.
 * ============================================================================ */

export type Price = Brand<string, 'Price'>;
export type Qty = Brand<string, 'Qty'>;
export type LotSize = Brand<number, 'LotSize'>;

/* ============================================================================
 * Time
 * ============================================================================ */

/** ISO-8601 timestamp in UTC (`...+00:00` or `...Z`). All wire timestamps are this. */
export type ISODateTime = Brand<string, 'ISODateTime'>;

/** Calendar date with no time component (YYYY-MM-DD). */
export type ISODate = Brand<string, 'ISODate'>;

/** UNIX epoch milliseconds. Used for charts that want a numeric x-axis. */
export type EpochMs = Brand<number, 'EpochMs'>;

/* ============================================================================
 * Constructors — runtime shape-checks only. Math goes through lib/math.
 * ============================================================================ */

function assertFinite(value: number, label: string): void {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${label} must be a finite number; got ${String(value)}`);
  }
}

export const inr = (value: number): INR => {
  assertFinite(value, 'INR');
  return value as INR;
};
export const usd = (value: number): USD => {
  assertFinite(value, 'USD');
  return value as USD;
};
export const money = <C extends string>(value: number, _currency: C): Money<C> => {
  assertFinite(value, `Money<${_currency}>`);
  return value as Money<C>;
};

export const bps = (value: number): BPS => {
  assertFinite(value, 'BPS');
  return value as BPS;
};
export const percent = (value: number): PercentPoints => {
  assertFinite(value, 'PercentPoints');
  if (value < 0 || value > 100) {
    throw new RangeError(`PercentPoints out of [0,100]: ${value}`);
  }
  return value as PercentPoints;
};
export const ratio = (value: number): Ratio => {
  assertFinite(value, 'Ratio');
  if (value < 0 || value > 1) {
    throw new RangeError(`Ratio out of [0,1]: ${value}`);
  }
  return value as Ratio;
};
export const lotMult = (value: number): LotMult => {
  assertFinite(value, 'LotMult');
  return value as LotMult;
};
export const qtyMult = (value: number): QtyMult => {
  assertFinite(value, 'QtyMult');
  return value as QtyMult;
};
export const lotSize = (value: number): LotSize => {
  assertFinite(value, 'LotSize');
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`LotSize must be a positive integer; got ${value}`);
  }
  return value as LotSize;
};

const PRICE_QTY_RE = /^-?\d+(\.\d+)?$/;
export const price = (value: string): Price => {
  if (!PRICE_QTY_RE.test(value)) {
    throw new TypeError(`Price must match decimal regex; got ${value}`);
  }
  return value as Price;
};
export const qty = (value: string): Qty => {
  if (!PRICE_QTY_RE.test(value)) {
    throw new TypeError(`Qty must match decimal regex; got ${value}`);
  }
  return value as Qty;
};

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
export const isoDateTime = (value: string): ISODateTime => {
  if (!ISO_DATETIME_RE.test(value)) {
    throw new TypeError(`ISODateTime must be ISO-8601 with TZ; got ${value}`);
  }
  return value as ISODateTime;
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const isoDate = (value: string): ISODate => {
  if (!ISO_DATE_RE.test(value)) {
    throw new TypeError(`ISODate must be YYYY-MM-DD; got ${value}`);
  }
  return value as ISODate;
};

export const epochMs = (value: number): EpochMs => {
  assertFinite(value, 'EpochMs');
  if (!Number.isInteger(value)) {
    throw new TypeError(`EpochMs must be an integer; got ${value}`);
  }
  return value as EpochMs;
};

/* ============================================================================
 * Unbrand helpers — explicit, audit-able. Use sparingly (display layer).
 * ============================================================================ */

export const rawNumber = (value: { valueOf(): number }): number => Number(value);
export const rawString = (value: string): string => value;

/** Casts a plain number to an INR-branded value WITHOUT runtime check.
 *  Reserved for hot loops (chart renderers) where the value has already
 *  been validated upstream by Zod / a constructor. Do not use in app code. */
export const _unsafeInr = (value: number): INR => value as INR;
export const _unsafeUsd = (value: number): USD => value as USD;
export const _unsafePrice = (value: string): Price => value as Price;
export const _unsafeQty = (value: string): Qty => value as Qty;
export const _unsafeIsoDateTime = (value: string): ISODateTime => value as ISODateTime;

/* ============================================================================
 * Type guards
 * ============================================================================ */

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const isISODateTimeString = (value: unknown): value is string =>
  typeof value === 'string' && ISO_DATETIME_RE.test(value);

export const isPriceString = (value: unknown): value is string =>
  typeof value === 'string' && PRICE_QTY_RE.test(value);
